// =====================================================================
// SafetyAlertBanner.jsx — M-010 : le bandeau qui ne se laisse pas fermer
// =====================================================================
// Demandé par Silviu : « un bandeau qui reste affiché jusqu'à ce qu'il
// fasse le nécessaire ». C'est le bon réflexe, et pas seulement en
// attendant le courriel : dans un entrepôt, les gens vivent dans
// l'application, pas dans Outlook. Le courriel prévient une fois ; le
// bandeau, lui, insiste.
//
// Il est monté dans App.jsx, sous la barre supérieure, donc il suit la
// personne sur TOUS les écrans. C'est un écart assumé par rapport aux
// autres bandeaux du projet (PeriodLockBanner, OvertimeBalanceBanner),
// qui sont posés page par page : ici, tout l'intérêt est qu'il trouve
// Vincent là où il travaille, sans qu'il ait à ouvrir le module.
//
// Ce qui l'empêche de devenir du papier peint :
//   · pas de bouton « fermer » — donc rien à cliquer pour l'ignorer ;
//     il s'éteint quand l'action est faite, pas quand on s'en agace
//   · il n'apparaît que s'il y a quelque chose d'échu ou dans sa fenêtre
//     de rappel ; un bandeau allumé en permanence n'est plus lu
//   · les échéances LÉGALES ne se replient pas ; le reste se replie mais
//     le compteur, lui, reste visible
// =====================================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { safetyAlerts } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';

const POLL_MS = 5 * 60_000;   // 5 min : ces échéances bougent en jours

const TON = {
  legal: {
    bg: '#fdf0f0', line: '#dc2626', fg: '#7f1d1d', chip: '#dc2626',
    mot: 'Délai légal',
  },
  urgent: {
    bg: '#fdf5e7', line: '#d97706', fg: '#7c2d12', chip: '#d97706',
    mot: 'En retard',
  },
  bientot: {
    bg: '#eef2ff', line: '#4338ca', fg: '#312e81', chip: '#4338ca',
    mot: 'À prévoir',
  },
};

// Combien de lignes on montre sans déplier. Au-delà, on annonce le reste :
// une liste de quinze lignes ne se lit pas, un compteur si.
const VISIBLES = 3;

export default function SafetyAlertBanner() {
  const { user, can } = useAuth();
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(false);

  // Inutile d'interroger l'API pour quelqu'un qui n'a aucun accès au
  // module : il n'aurait de toute façon rien à faire de ces échéances.
  const concerne = can('safety.declare.own') || can('safety.read.all')
    || can('maintenance.execute.own') || can('maintenance.plan.all');

  useEffect(() => {
    if (!user || !concerne) return undefined;
    let mort = false;

    const charger = async () => {
      try {
        const d = await safetyAlerts.mine();
        if (!mort) setData(d);
      } catch {
        // Silencieux : le bandeau est un rappel, pas une fonction critique.
        // Les délais eux-mêmes sont tenus côté serveur.
        if (!mort) setData(null);
      }
    };

    charger();
    const t = setInterval(charger, POLL_MS);
    return () => { mort = true; clearInterval(t); };
  }, [user, concerne]);

  if (!data || !data.counts?.total) return null;

  const items = data.items || [];
  const pire = items[0]?.severity || 'bientot';
  const ton = TON[pire] || TON.bientot;
  // Une échéance légale ne se replie jamais : on ne cache pas ce qui se
  // paie devant l'assureur ou l'inspection.
  const bloque = pire === 'legal';
  const montres = (ouvert || bloque) ? items : items.slice(0, VISIBLES);
  const restants = items.length - montres.length;

  return (
    <div role="status" style={{
      background: ton.bg, borderBottom: `1px solid ${ton.line}33`,
      borderLeft: `4px solid ${ton.line}`, color: ton.fg,
      padding: '.6rem .9rem', fontSize: '.88rem',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: '.6rem',
        flexWrap: 'wrap', marginBottom: montres.length ? '.35rem' : 0,
      }}>
        <span style={{
          background: ton.chip, color: '#fff', borderRadius: 999,
          padding: '1px 9px', fontSize: '.74rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '.03em',
        }}>{ton.mot}</span>
        <b>Sécurité — {data.counts.total} point(s) en attente de vous</b>
        {data.counts.legal > 0 && (
          <span style={{ fontSize: '.82rem' }}>
            dont <b>{data.counts.legal}</b> à échéance légale
          </span>
        )}
        {/* Les echeances legales du registre menent aux accidents ; tout
            le reste (controles, actions, certifications) aux verifications. */}
        <Link to={pire === 'legal' ? '/accidents' : '/securite'} style={{
          marginLeft: 'auto', color: ton.line, fontWeight: 700,
          textDecoration: 'none', whiteSpace: 'nowrap',
        }}>{pire === 'legal' ? 'Ouvrir le registre →' : 'Ouvrir Sécurité →'}</Link>
      </div>

      <ul style={{ margin: 0, paddingLeft: '1.1rem', listStyle: 'disc' }}>
        {montres.map((i, n) => (
          <li key={`${i.kind}-${i.ref || n}`} style={{ padding: '1px 0' }}>
            <b>{i.title}</b>
            {i.detail && (
              <span style={{ opacity: .85 }}> — {i.detail}</span>
            )}
          </li>
        ))}
      </ul>

      {restants > 0 && (
        <button type="button" onClick={() => setOuvert(true)} style={{
          border: 'none', background: 'transparent', color: ton.line,
          fontWeight: 600, fontSize: '.82rem', cursor: 'pointer',
          padding: '.2rem 0 0', textDecoration: 'underline',
        }}>
          et {restants} autre(s) — tout afficher
        </button>
      )}
      {ouvert && !bloque && items.length > VISIBLES && (
        <button type="button" onClick={() => setOuvert(false)} style={{
          border: 'none', background: 'transparent', color: ton.line,
          fontWeight: 600, fontSize: '.82rem', cursor: 'pointer',
          padding: '.2rem 0 0', textDecoration: 'underline',
        }}>
          replier
        </button>
      )}
    </div>
  );
}
